import {Switch, TextInput} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useCallback} from 'react';

import {ALLOWED_TIMEZONE} from 'graphql-client/__generated/GeneratedEnumMaps';
import HelpButton from 'help/HelpButton';
import {AdapterField, ConnectionFieldType} from 'instruments/connections/ConnectionConstants';
import {ValidationInfo, ValidationInfoPopover} from 'schemas/tags/EditableSchemaTag';
import BaseEditableField from 'ui-library/BaseEditableField';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import SecureTextInput from 'ui-library/inputs/SecureTextInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import UrlAwareText from 'ui-library/UrlAwareText';

interface ConnectionFieldsFormProps {
  fields: Array<AdapterField>;
  values: Record<string, string>;
  fieldInfos: Record<string, string | null>;
  fieldValidationInfos?: Record<string, ValidationInfo>;
  isWritable: boolean;
  isCreating: boolean;
  hasCredentialsForAutoSync?: boolean;
  onChange: (fieldId: string, value: string) => void;
}

const readOnlyRootStyle = {pointerEvents: 'none' as const};

function ConnectionFieldsForm(props: ConnectionFieldsFormProps): JSX.Element {
  const {
    fields,
    values,
    fieldInfos,
    fieldValidationInfos,
    isWritable,
    isCreating,
    hasCredentialsForAutoSync,
    onChange,
  } = props;

  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');

  const startEditing = useCallback(
    (fieldId: string) => {
      setEditingFieldId(fieldId);
      setEditingValue(values[fieldId] ?? '');
    },
    [values]
  );

  const completeEditing = useCallback(
    (fieldId: string) => {
      onChange(fieldId, editingValue);
      setEditingFieldId(null);
    },
    [editingValue, onChange]
  );

  const cancelEditing = useCallback(() => {
    setEditingFieldId(null);
  }, []);

  function renderTimezoneEditor(field: AdapterField): JSX.Element {
    const dropdown = (
      <SelectDropdown
        menu={
          <SearchableMenu
            selectedValue={isCreating ? (values[field.id] ?? '') : editingValue}
            optionDatas={Object.values(ALLOWED_TIMEZONE).map((tz) => ({
              name: tz,
              value: tz,
            }))}
            onSelect={(timezone: string | null) => {
              if (isCreating) {
                onChange(field.id, timezone ?? '');
              } else {
                setEditingValue(timezone ?? '');
              }
            }}
            height={Menu.HEIGHTS.MEDIUM}
            width={Menu.WIDTHS.LARGE}
            scrollable={true}
          />
        }
        displayName={isCreating ? (values[field.id] ?? '') : editingValue}
      />
    );

    if (isCreating) {
      return dropdown;
    }

    return (
      <BaseEditableField
        isWritable={isWritable}
        isBeingEdited={editingFieldId === field.id}
        isCompleteEnabled={true}
        isFullWidth={true}
        viewer={
          <div className="editableField-text">
            {values[field.id] ? <UrlAwareText textValue={values[field.id]} /> : '\u00a0'}
          </div>
        }
        editor={<div className={css(styles.editorWrapper)}>{dropdown}</div>}
        onEdit={() => {
          startEditing(field.id);
        }}
        onComplete={() => {
          completeEditing(field.id);
        }}
        onCancel={cancelEditing}
      />
    );
  }

  function renderSwitchField(field: AdapterField): JSX.Element {
    if (!isWritable) {
      return (
        <div className="editableField-text">
          <Tooltip text={values[field.id] === 'true' ? 'Enabled (true)' : 'Disabled (false)'}>
            <Switch
              checked={values[field.id] === 'true'}
              readOnly={true}
              styles={{root: readOnlyRootStyle}}
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="editableField-text">
        <Switch
          checked={values[field.id] === 'true'}
          onChange={(event) => {
            onChange(field.id, String(event.currentTarget.checked));
          }}
        />
      </div>
    );
  }

  function renderAutoSyncField(field: AdapterField): JSX.Element {
    if (!isWritable) {
      return (
        <div className="editableField-text">
          <Tooltip text={values[field.id] === 'true' ? 'Enabled (true)' : 'Disabled (false)'}>
            <Switch
              checked={values[field.id] === 'true'}
              readOnly={true}
              styles={{root: readOnlyRootStyle}}
            />
          </Tooltip>
        </div>
      );
    }

    return (
      <div className="editableField-text">
        <Tooltip
          text="Per-user login must be disabled, or username and password must be set to enable auto-sync"
          isDisabled={hasCredentialsForAutoSync}
        >
          <Switch
            checked={values[field.id] === 'true'}
            disabled={!hasCredentialsForAutoSync}
            onChange={(event) => {
              onChange(field.id, String(event.currentTarget.checked));
            }}
          />
        </Tooltip>
      </div>
    );
  }

  function renderSecretField(field: AdapterField): JSX.Element {
    const secureInput = (
      <SecureTextInput
        name={field.displayName}
        value={(isCreating ? (values[field.id] ?? '') : editingValue) || null}
        onChange={(newValue: string) => {
          if (isCreating) {
            onChange(field.id, newValue);
          } else {
            setEditingValue(newValue);
          }
        }}
        disabled={false}
        description={`After saving ${field.displayName.toLowerCase()} this will no longer be visible.`}
      />
    );

    if (isCreating) {
      return secureInput;
    }

    return (
      <BaseEditableField
        isWritable={isWritable}
        isBeingEdited={editingFieldId === field.id}
        isCompleteEnabled={true}
        isFullWidth={true}
        viewer={<div className="editableField-text">{values[field.id] ? '••••••' : '\u00a0'}</div>}
        editor={<div className={css(styles.editorWrapper)}>{secureInput}</div>}
        onEdit={() => {
          startEditing(field.id);
        }}
        onComplete={() => {
          completeEditing(field.id);
        }}
        onCancel={cancelEditing}
      />
    );
  }

  function renderGatewayField(field: AdapterField): JSX.Element | null {
    if (!isCreating) {
      return null;
    }
    return <span>{values[field.id]}</span>;
  }

  function renderTextField(field: AdapterField): JSX.Element {
    const textInput = (
      <TextInput
        size="md"
        value={isCreating ? (values[field.id] ?? '') : editingValue}
        onChange={(e) => {
          if (isCreating) {
            onChange(field.id, e.target.value);
          } else {
            setEditingValue(e.target.value);
          }
        }}
      />
    );

    if (isCreating) {
      return textInput;
    }

    return (
      <BaseEditableField
        isWritable={isWritable}
        isBeingEdited={editingFieldId === field.id}
        isCompleteEnabled={true}
        isFullWidth={true}
        viewer={
          <div className="editableField-text">
            {values[field.id] ? <UrlAwareText textValue={values[field.id]} /> : '\u00a0'}
          </div>
        }
        editor={<div className={css(styles.editorWrapper)}>{textInput}</div>}
        onEdit={() => {
          startEditing(field.id);
        }}
        onComplete={() => {
          completeEditing(field.id);
        }}
        onCancel={cancelEditing}
      />
    );
  }

  function renderFieldEditor(field: AdapterField): JSX.Element | null {
    if (field.id === 'default_timezone') {
      return renderTimezoneEditor(field);
    }
    if (field.id === 'per_user_login_required') {
      return renderSwitchField(field);
    }
    if (field.id === 'enable_auto_sync') {
      return renderAutoSyncField(field);
    }
    if (field.fieldType === ConnectionFieldType.SECRET_REF) {
      return renderSecretField(field);
    }
    if (field.fieldType === ConnectionFieldType.GATEWAY) {
      return renderGatewayField(field);
    }
    return renderTextField(field);
  }

  return (
    <Row>
      {fields.map((field) => {
        const editor = renderFieldEditor(field);
        if (editor == null) {
          return null;
        }

        return (
          <Col key={field.id} xs={6}>
            <FieldLabel
              label={field.displayName}
              isRequired={field.isRequired}
              labelStyle={LabelStyle.LABEL}
            />
            {fieldValidationInfos?.[field.id] && (
              <ValidationInfoPopover validationInfo={fieldValidationInfos[field.id]} />
            )}
            {fieldInfos[field.id] && (
              <HelpButton renderContent={() => fieldInfos[field.id]!} iconClassName="u-mini-spacing-left" />
            )}
            {editor}
          </Col>
        );
      })}
    </Row>
  );
}

const styles = StyleSheet.create({
  editorWrapper: {
    flex: 1,
    minWidth: 0,
  },
});

export default ConnectionFieldsForm;
